import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Truck, X } from 'lucide-react';
import { getActiveOrder, clearActiveOrder } from '../utils/activeOrder';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FINAL_STATUSES = ['delivered', 'cancelled'];
const STAFF_ROLES = ['admin', 'super_admin', 'driver', 'restaurant_manager', 'pos'];

/**
 * Sticky banner shown across the app whenever the user has an active order.
 * Lets the user always return to the /order-tracking page even after
 * navigating away or closing the browser.
 *
 * Hidden when:
 *  - logged in as staff (admin/driver/restaurant_manager) — banner is for customers only
 *  - on the tracking page itself
 *  - on staff routes (/admin, /driver, /pos)
 *  - order is delivered or cancelled
 */
export const ActiveOrderBanner = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  const [active, setActive] = useState(getActiveOrder());
  const [status, setStatus] = useState(null);

  // Re-read localStorage whenever the route or user changes
  useEffect(() => {
    setActive(getActiveOrder());
  }, [location.pathname, user?.user_id]);

  // Poll the order so the banner can disappear when delivered/cancelled
  useEffect(() => {
    if (!active?.orderId) return;
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const endpoint = active.orderType === 'shisha'
          ? `${API_URL}/api/shisha-rentals/${active.orderId}`
          : active.orderType === 'ice'
            ? `${API_URL}/api/ice-orders/${active.orderId}`
            : `${API_URL}/api/orders/${active.orderId}`;
        const r = await fetch(endpoint);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        setStatus(data.status);
        if (FINAL_STATUSES.includes(data.status)) {
          // auto-clear after 30s on delivered so user still sees it briefly
          setTimeout(() => {
            clearActiveOrder();
            setActive(null);
          }, 30000);
        }
      } catch (e) {
        // ignore
      }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [active?.orderId, active?.orderType]);

  // Hide on the tracking page itself, on staff routes, and for staff users
  if (!active?.orderId) return null;
  if (user && STAFF_ROLES.includes(user.role)) return null;
  if (location.pathname.startsWith('/admin')) return null;
  if (location.pathname.startsWith('/driver')) return null;
  if (location.pathname.startsWith('/pos')) return null;
  if (location.pathname.startsWith(`/order-tracking/${active.orderId}`)) return null;
  if (FINAL_STATUSES.includes(status)) return null;

  const labels = {
    received: language === 'pt' ? 'Recebido' : 'Received',
    preparing: language === 'pt' ? 'Em preparação' : 'Preparing',
    ready: language === 'pt' ? 'Pronto' : 'Ready',
    out_for_delivery: language === 'pt' ? 'A caminho' : 'Out for delivery',
  };
  const statusLabel = status ? (labels[status] || status) : '...';
  const trackUrl = active.orderType === 'food'
    ? `/order-tracking/${active.orderId}`
    : `/order-tracking/${active.orderId}?type=${active.orderType}`;

  return (
    <div
      className="sticky top-16 z-40 bg-[#FF6B35] text-white shadow-md"
      data-testid="active-order-banner"
    >
      <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <Link to={trackUrl} className="flex items-center gap-3 flex-1 min-w-0">
          <Truck size={18} className="shrink-0" />
          <div className="text-sm truncate">
            <span className="font-semibold">
              {language === 'pt' ? 'Pedido em curso' : 'Order in progress'}
            </span>
            <span className="opacity-80 ml-2">
              #{active.orderId.slice(-8).toUpperCase()} — {statusLabel}
            </span>
          </div>
          <span className="hidden sm:inline-block bg-white/20 hover:bg-white/30 transition-colors px-3 py-1 rounded-full text-xs font-semibold ml-auto">
            {language === 'pt' ? 'Acompanhar' : 'Track'} →
          </span>
        </Link>
        <button
          onClick={() => { clearActiveOrder(); setActive(null); }}
          className="p-1 rounded-full hover:bg-white/20 transition-colors"
          aria-label="dismiss"
          data-testid="active-order-banner-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default ActiveOrderBanner;
